"use client";

import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/components/tooltip";
import { setCookie } from "cookies-next";
import { Info } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import {
  calculateBuyingPower,
  formatCurrencyWithoutCents,
  maskCurrencyWithoutCents,
} from "../../../../utils/common";
import Image from "next/image";
import {
  PostHostEventsEnum,
  sendPostHogEvent,
} from "../../../../libs/post-hog/post-hog-send-event";
import { getCookie } from "cookies-next";

export const FinancingCalculatorForm = ({ image }: { image: string }) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const [downPayment, setDownPayment] = useState("");
  const [installment, setInstallment] = useState("");
  const [buyingPower, setBuyingPower] = useState<string>("0");

  const handleDownPaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maskedValue = maskCurrencyWithoutCents(value);
    setDownPayment(maskedValue);

    const numericDownPayment = Number(maskedValue.replace(/\D/g, ""));
    const numericInstallment = Number(installment.replace(/\D/g, ""));

    if (!isNaN(numericDownPayment) && !isNaN(numericInstallment)) {
      const estimatedBuyingPower = calculateBuyingPower(
        numericDownPayment,
        numericInstallment
      );
      setBuyingPower(formatCurrencyWithoutCents(estimatedBuyingPower));
    }
  };

  const handleInstallmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maskedValue = maskCurrencyWithoutCents(value);
    setInstallment(maskedValue);

    const numericDownPayment = Number(downPayment.replace(/\D/g, ""));
    const numericInstallment = Number(maskedValue.replace(/\D/g, ""));

    if (!isNaN(numericDownPayment) && !isNaN(numericInstallment)) {
      const estimatedBuyingPower = calculateBuyingPower(
        numericDownPayment,
        numericInstallment
      );
      setBuyingPower(formatCurrencyWithoutCents(estimatedBuyingPower));
    }
  };

  const handleBlur = () => {
    const rawDownPayment = Number(downPayment.replace(/\D/g, ""));
    const rawInstallment = Number(installment.replace(/\D/g, ""));

    setCookie("downPayment", rawDownPayment.toString(), {
      maxAge: 60 * 60 * 24 * 60,
    });
    setCookie("installment", rawInstallment.toString(), {
      maxAge: 60 * 60 * 24 * 60,
    });
  };

  const handleNavigate = async () => {
    const price = parseFloat(buyingPower.replace(/\D/g, ""));
    const priceMinusFivePercent = Math.floor(price - price * 0.05);
    const pricePlusTenPercent = Math.ceil(price + price * 0.1);

    const email = (await getCookie("email")) ?? undefined;
    const downPayment = (await getCookie("downPayment")) ?? undefined;
    const installment = (await getCookie("installment")) ?? undefined;

    sendPostHogEvent({
      eventName: PostHostEventsEnum.USER_FINANCING_CALCULATOR,
      email,
      eventProperties: {
        estimated_vehicle_price: +price,
        down_payment: Number(downPayment ?? 0),
        installment: Number(installment ?? 0),
      },
    });

    push(
      `/listing?minPreco=${priceMinusFivePercent}&maxPreco=${pricePlusTenPercent}`
    );
  };

  return (
    <div className="mx-auto mb-12 max-w-[1430px]">
      <section className="relative">
        <div className="px-[15px] static w-full">
          <section className="py-12 px-4 lg:px-36 rounded-xl bg-white">
            <div className="flex flex-col md:flex-row gap-5 lg:gap-24">
              <div className="md:w-3/6">
                <Image
                  src={image}
                  alt="car-banner-financing"
                  className="h-auto"
                  width={500}
                  height={500}
                />
              </div>
              <div className="md:w-3/6">
                <div className="w-full">
                  <Label className="dark:text-black" htmlFor="down-payment">
                    Valor de entrada
                  </Label>
                  <Input
                    type="text"
                    id="down-payment"
                    maxLength={15}
                    className="mt-0.5 pl-4 dark:text-black"
                    value={downPayment}
                    onChange={handleDownPaymentChange}
                    onBlur={handleBlur}
                    placeholder="R$ 0"
                  />
                </div>
                <div className="w-full mt-4">
                  <Label className="dark:text-black" htmlFor="installment">
                    Valor máx. da parcela
                  </Label>
                  <Input
                    type="text"
                    id="installment"
                    maxLength={15}
                    className="mt-0.5 pl-4 dark:text-black"
                    value={installment}
                    onChange={handleInstallmentChange}
                    onBlur={handleBlur}
                    placeholder="R$ 0"
                  />
                </div>
                <div className="mt-4 rounded-xl px-5 py-2 flex flex-col sm:flex-row gap-2 justify-between items-center">
                  <div className="flex flex-col justify-center items-center sm:items-start relative w-fit dark:text-black">
                    <span className="text-sm">
                      Cálculo estimado do veículo:
                    </span>
                    <p className="text-4xl font-sans font-bold">
                      <small className="text-sm">R$ </small>
                      {buyingPower}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="absolute top-0.5 -right-5">
                          <Info
                            size={15}
                            className="cursor-pointer text-primary-600"
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-80">
                          Estimativa baseada em um bom crédito, prazo de
                          financiamento de 60 meses, entrada de ~20% com taxa de
                          juros de 2,85% ao mês. Os valores podem variar.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    className="w-full dark:bg-black dark:text-white dark:hover:bg-gray-200 dark:hover:text-black"
                    variant="secondary"
                    onClick={handleNavigate}
                  >
                    Buscar {pathname.includes("/motos") ? "motos" : "carros"}{" "}
                    nessa faixa
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};
